import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { getApiErrorMessage } from '../api';
import { useAuth } from '../hooks/useAuth';
import ModalShell from '../components/ModalShell';
import StageUpdateModal from '../components/StageUpdateModal';
import ApplicationDetailPanel from '../components/ApplicationDetailPanel';
import SearchableCandidateSelect from '../components/SearchableCandidateSelect';
import SearchableJobSelect from '../components/SearchableJobSelect';
import MultiSelectFilter from '../components/MultiSelectFilter';
import ListFilterBar from '../components/ListFilterBar';
import QuickFilterGrid from '../components/QuickFilterGrid';
import FilterSearchField from '../components/FilterSearchField';
import { OFFER_STATUS_COLORS } from '../constants/offer';

import { patchApplicationStage, submitApplicationToClient } from '../utils/stageUpdate';
import {
  appCurrentStatusLabel,
  appMatchesStatusFilters,
  CURRENT_STATUS_FILTER_OPTIONS
} from '../utils/applicationCurrentStatus';
import TableExportButton from '../components/TableExportButton';
import { applicationColumns, mapApplications } from '../utils/dashboardColumns';
import { ResumePreviewModal } from '../components/ResumePreviewFrame';
import CandidatePhoneLink from '../components/CandidatePhoneLink';
import { mergeApplicationsWithCandidates } from '../utils/applicationDisplay';
import SortableTh from '../components/SortableTh';
import { useSortableTable } from '../utils/sortableTable';
import { APPLICATION_SORT_COLUMNS } from '../constants/listTableSort';
import { uploadUrl } from '../utils/uploads';
import { ActionMenu, ActionMenuItem } from '../components/ActionMenu';
import CandidateHistoryModal from '../components/CandidateHistoryModal';
import CandidateAvatarWithCompletion from '../components/CandidateAvatarWithCompletion';
import CandidateProfileModal from '../components/CandidateProfileModal';
import JobProfileModal from '../components/JobProfileModal';
import ClientProfileModal from '../components/ClientProfileModal';
import MatchAnalyticsModal from '../components/MatchAnalyticsModal';
import {
  PORTAL_ATS_TABLE,
  PORTAL_BADGE,
  PORTAL_BTN_PRIMARY,
  PORTAL_BTN_SECONDARY,
  PORTAL_LIST_PAGE,
  PORTAL_LIST_TABLE_SCROLL,
  PORTAL_PAGE_HEADER,
  PORTAL_STICKY_ACTIONS_TD,
  PORTAL_STICKY_ACTIONS_TH,
  PORTAL_TABLE_CARD_STICKY
} from '../constants/portalUi';

import {
  STAGES,
  STAGE_LABELS,
  TERMINAL_STAGES
} from '../constants/hiring';

const STAGE_COLORS = {
  submitted: 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))] border-[hsl(var(--ds-primary)/0.25)]',
  selected: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  interview: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  shortlisted: 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))] border-[hsl(var(--ds-primary)/0.25)]',
  offer: 'bg-purple-100 text-purple-700 border-purple-200',
  joined: 'bg-green-100 text-green-700 border-green-200',
  relieved: 'bg-orange-100 text-orange-700 border-orange-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  not_selected: 'bg-rose-100 text-rose-800 border-rose-200'
};

export default function Applications({ initialStage = 'submitted' }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [clientFilters, setClientFilters] = useState([]);
  const [activeStage, setActiveStage] = useState(initialStage);
  const [statusFilters, setStatusFilters] = useState([]);
  const [jobFilters, setJobFilters] = useState([]);
  const [listResumePreview, setListResumePreview] = useState(null);
  const [matchAnalytics, setMatchAnalytics] = useState(null);
  const [matchAnalyticsLoadingId, setMatchAnalyticsLoadingId] = useState(null);
  const [historyCandidate, setHistoryCandidate] = useState(null);
  const [profileCandidateId, setProfileCandidateId] = useState(null);
  const [profileJobId, setProfileJobId] = useState(null);
  const [profileClientId, setProfileClientId] = useState(null);
  const canOpenProfile =
    user?.role === 'admin' || user?.role === 'recruiter' || user?.role === 'client';
  const canOpenStaffForms = user?.role === 'admin' || user?.role === 'recruiter';
  const canViewMatchAnalytics = canOpenProfile;
  const profileReadOnly = user?.role === 'client';

  // Data fetching
  const { data: applications, isLoading: appsLoading, error: appsError } = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.get('/applications').then(r => r.data),
    enabled: !!user,
    staleTime: 30_000
  });
  const staffUsesCandidateApi =
    user?.role === 'admin' || user?.role === 'recruiter' || user?.role === 'client';

  const { data: candidates } = useQuery({
    queryKey: ['candidates'],
    queryFn: () => api.get('/candidates').then(r => r.data),
    enabled: !!user && staffUsesCandidateApi
  });

  const applicationsWithContacts = useMemo(
    () => mergeApplicationsWithCandidates(applications, candidates),
    [applications, candidates]
  );
  const { data: jobs } = useQuery({ 
    queryKey: ['jobs'], 
    queryFn: () => api.get('/jobs').then(r => r.data),
    enabled: !!user && (user.role === 'admin' || user.role === 'recruiter')
  });
  const { data: clients } = useQuery({ 
    queryKey: ['clients'], 
    queryFn: () => api.get('/clients').then(r => r.data),
    enabled: !!user && (user.role === 'admin' || user.role === 'recruiter')
  });

  // Get unique values for filters
  const uniqueClients = useMemo(() => {
    if (!applications) return [];
    const clientSet = new Set();
    applications.forEach(app => {
      if (app.clientName) clientSet.add(app.clientName);
    });
    return Array.from(clientSet).sort();
  }, [applications]);

  const uniqueJobs = useMemo(() => {
    if (!applications) return [];
    const jobSet = new Set();
    applications.forEach(app => {
      if (app.jobTitle) jobSet.add(app.jobTitle);
    });
    return Array.from(jobSet).sort();
  }, [applications]);

  const clientFilterOptions = useMemo(
    () => uniqueClients.map(c => ({ value: c, label: c })),
    [uniqueClients]
  );

  const jobFilterOptions = useMemo(
    () => uniqueJobs.map(j => ({ value: j, label: j })),
    [uniqueJobs]
  );

  const activePipelineStages = useMemo(
    () => STAGES.filter(s => !TERMINAL_STAGES.has(s)),
    []
  );

  const closedPipelineStages = useMemo(
    () => STAGES.filter(s => TERMINAL_STAGES.has(s)),
    []
  );

  const sidebarStages = STAGES;

  const filteredWithoutStage = useMemo(() => {
    const source = applicationsWithContacts ?? applications;
    if (!source?.length) return [];
    return source.filter(app => {
      if (clientFilters.length && !clientFilters.includes(app.clientName)) return false;
      if (statusFilters.length && !appMatchesStatusFilters(app, statusFilters)) return false;
      if (jobFilters.length && !jobFilters.includes(app.jobTitle)) return false;

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          (app.id || '').toLowerCase().includes(query) ||
          (app.jobTitle || '').toLowerCase().includes(query) ||
          (app.candidateName || app.candidateId || '').toLowerCase().includes(query) ||
          (app.candidateEmail || '').toLowerCase().includes(query) ||
          (app.candidatePhone || '').toLowerCase().includes(query) ||
          (app.clientName || '').toLowerCase().includes(query) ||
          (app.stage || '').toLowerCase().includes(query) ||
          (app.statusNotes || '').toLowerCase().includes(query) ||
          (app.offerStatusLabel || '').toLowerCase().includes(query) ||
          (app.interviewStatusLabel || '').toLowerCase().includes(query) ||
          appCurrentStatusLabel(app).toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [
    applications,
    applicationsWithContacts,
    clientFilters,
    statusFilters,
    jobFilters,
    searchQuery
  ]);

  const stageCounts = useMemo(() => {
    const counts = Object.fromEntries(sidebarStages.map(s => [s, 0]));
    for (const app of filteredWithoutStage) {
      if (counts[app.stage] != null) counts[app.stage] += 1;
    }
    return counts;
  }, [filteredWithoutStage, sidebarStages]);

  const filteredData = useMemo(() => {
    if (!activeStage) return filteredWithoutStage;
    return filteredWithoutStage.filter(app => app.stage === activeStage);
  }, [filteredWithoutStage, activeStage]);

  const statusFilterOptions = CURRENT_STATUS_FILTER_OPTIONS;

  const { sort, toggleSort, sortedRows: sortedApplications } = useSortableTable(
    filteredData,
    APPLICATION_SORT_COLUMNS
  );

  // Modals
  const [form, setForm] = useState({ candidateId: '', jobId: '', note: '' });
  const [stageModal, setStageModal] = useState(null);
  const [detailApp, setDetailApp] = useState(null);
  const [stageError, setStageError] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const mutation = useMutation({
    mutationFn: ({ id, ...payload }) => patchApplicationStage(id, payload),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      setStageModal(null);
      setStageError('');
      if (detailApp?.id === variables?.id) {
        setDetailApp(prev => (prev ? { ...prev, ...data, stage: data.stage ?? variables.stage } : prev));
      }
    },
    onError: err => {
      setStageError(err.response?.data?.message || 'Failed to update stage');
    }
  });

  const createApp = useMutation({
    mutationFn: payload => api.post('/applications', payload).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      setForm({ candidateId: '', jobId: '', note: '' });
      setShowSubmitModal(false);
    }
  });

  const submitToClientMutation = useMutation({
    mutationFn: ({ id, note }) => submitApplicationToClient(id, note),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      setStageModal(null);
      setStageError('');
      if (detailApp?.id === variables?.id) {
        setDetailApp(prev => (prev ? { ...prev, ...data } : prev));
      }
    },
    onError: err => {
      setStageError(err.response?.data?.message || 'Failed to submit to client');
    }
  });

  const deleteApp = useMutation({
    mutationFn: id => api.delete(`/applications/${encodeURIComponent(id)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      if (detailApp?.id) setDetailApp(null);
      if (stageModal?.id) setStageModal(null);
    },
    onError: err => {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.message;
      if (status === 404 && !serverMsg) {
        alert(
          'Delete is not available on the running API (old server). Stop the server on port 4000, run npm run dev in the server folder, then try again.'
        );
        return;
      }
      alert(serverMsg || err.message || 'Failed to delete application');
    }
  });

  const canManageAts = user && (user.role === 'admin' || user.role === 'recruiter');

  const openMatchAnalytics = async (app, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!app?.id || !app.candidateId || !app.jobId || app.candidateMissing) return;
    setMatchAnalyticsLoadingId(app.id);
    try {
      let data;
      try {
        ({ data } = await api.get(`/applications/${app.id}/match-analytics`));
      } catch (appErr) {
        if (appErr.response?.status === 404) {
          ({ data } = await api.get('/match', {
            params: { jobId: app.jobId, candidateId: app.candidateId }
          }));
        } else {
          throw appErr;
        }
      }

      let matchRow = data?.match;
      if (!matchRow && Array.isArray(data?.matches)) {
        matchRow = data.matches.find(
          row =>
            row.candidate?.id === app.candidateId ||
            row.candidate?.userId === app.candidateId
        );
      }
      if (!matchRow) {
        alert('Match analytics could not be loaded for this application.');
        return;
      }

      setMatchAnalytics({
        matchRow,
        jobRequirements: data.job?.requirements,
        methodology: data.methodology
      });
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Could not load match analytics');
    } finally {
      setMatchAnalyticsLoadingId(null);
    }
  };

  const confirmDeleteApplication = app => {
    const candidateLabel = app.candidateName || app.candidateId || 'this candidate';
    const jobLabel = app.jobTitle || app.jobId || 'this job';
    const msg = app.candidateMissing
      ? `Remove ATS entry ${app.id} for ${jobLabel}? The candidate profile was already deleted.`
      : `Remove ATS entry ${app.id} (${candidateLabel} → ${jobLabel}) from the board?`;
    if (window.confirm(msg)) deleteApp.mutate(app.id);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setClientFilters([]);
    setStatusFilters([]);
    setJobFilters([]);
    setActiveStage('submitted');
  };

  const hasActiveFilters =
    searchQuery ||
    clientFilters.length > 0 ||
    statusFilters.length > 0 ||
    jobFilters.length > 0;

  if (!user) return <div className="p-6">Loading...</div>;
  if (appsLoading) return <div className="p-6">Loading applications...</div>;
  if (appsError) {
    return (
      <div className="p-6">
        <div className="text-red-600">
          {getApiErrorMessage(appsError, 'Error loading applications. Please try again.')}
        </div>
        <button
          type="button"
          className={`mt-4 ${PORTAL_BTN_PRIMARY}`}
          onClick={() => qc.invalidateQueries({ queryKey: ['applications'] })}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`${PORTAL_LIST_PAGE} applications-list-page`}>
      <div className="flex-shrink-0 flex items-center justify-between gap-4">
        <h1 className={PORTAL_PAGE_HEADER}>ATS Board</h1>
        <div className="flex items-center gap-2">
          <TableExportButton
            filename="applications"
            sheetName="Applications"
            columns={applicationColumns}
            rows={mapApplications(sortedApplications)}
          />
          {user && (user.role === 'admin' || user.role === 'recruiter') && (
            <button
              type="button"
              className={PORTAL_BTN_PRIMARY}
              onClick={() => setShowSubmitModal(true)}
            >
              Submit Candidate
            </button>
          )}
        </div>
      </div>

      <div className="flex-shrink-0">
      <ListFilterBar
        footer={
          hasActiveFilters ? (
            <div className="mt-2 flex items-center justify-between text-xs text-[hsl(var(--ds-muted-fg))]">
              <span>
                Showing {sortedApplications.length} in {STAGE_LABELS[activeStage] || activeStage}
                {hasActiveFilters ? ` · ${filteredWithoutStage.length} match filters` : ''}
              </span>
              <button type="button" className="text-[hsl(var(--ds-primary))] hover:underline" onClick={clearFilters}>
                Clear all filters
              </button>
            </div>
          ) : null
        }
      >
        <QuickFilterGrid
          searchPosition="start"
          filtersAlign="end"
          searchFlex="md:flex-none md:w-[9rem] md:min-w-[8rem] md:max-w-[9.5rem]"
          filterFlex="md:flex-none md:w-[7.75rem] md:min-w-[7rem] md:max-w-[8rem]"
          filterFlexes={[
            'md:flex-none md:w-[8.75rem] md:min-w-[8rem] md:max-w-[9rem]',
            'md:flex-none md:w-[7.5rem] md:min-w-[6.75rem] md:max-w-[7.75rem]',
            'md:flex-none md:w-[7rem] md:min-w-[6.25rem] md:max-w-[7.25rem]'
          ]}
          search={
            <FilterSearchField
              placeholder="Search…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery('')}
            />
          }
          filters={[
            <MultiSelectFilter
              key="status"
              options={statusFilterOptions}
              values={statusFilters}
              onChange={setStatusFilters}
              emptyLabel="Current Status"
              searchable
              menuMinWidth={300}
              compact={false}
            />,
            <MultiSelectFilter
              key="client"
              options={clientFilterOptions}
              values={clientFilters}
              onChange={setClientFilters}
              emptyLabel="All Companies"
            />,
            <MultiSelectFilter
              key="job"
              options={jobFilterOptions}
              values={jobFilters}
              onChange={setJobFilters}
              emptyLabel="All Jobs"
            />
          ]}
        />
      </ListFilterBar>
      </div>

      <div className="flex-1 min-h-0 flex gap-4 min-w-0">
        <aside className="w-44 shrink-0 bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-xl shadow-sm overflow-hidden flex flex-col min-h-0">
          <div className="px-3 py-2 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] flex-shrink-0">
            <p className="text-xs font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide">Stages</p>
          </div>
          <nav className="flex-1 min-h-0 overflow-y-auto p-1.5 space-y-0.5">
            {activePipelineStages.map(stage => {
              const count = stageCounts[stage] || 0;
              const isActive = activeStage === stage;
              return (
                <button
                  key={stage}
                  type="button"
                  onClick={() => setActiveStage(stage)}
                  className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 text-left text-sm rounded-lg transition-colors ${
                    isActive
                      ? `${STAGE_COLORS[stage] || 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]'} font-medium border`
                      : 'text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] border border-transparent'
                  }`}
                >
                  <span className="truncate">{STAGE_LABELS[stage] || stage}</span>
                  <span
                    className={`text-xs tabular-nums shrink-0 px-1.5 py-0.5 rounded-full ${
                      isActive ? 'bg-[hsl(var(--ds-surface))]/70' : 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
            <div className="pt-2 mt-1 border-t border-[hsl(var(--ds-border))]">
              <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                Closed
              </p>
              {closedPipelineStages.map(stage => {
                const count = stageCounts[stage] || 0;
                const isActive = activeStage === stage;
                return (
                  <button
                    key={stage}
                    type="button"
                    onClick={() => setActiveStage(stage)}
                    className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 text-left text-sm rounded-lg transition-colors ${
                      isActive
                        ? `${STAGE_COLORS[stage] || 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]'} font-medium border`
                        : 'text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))] border border-transparent'
                    }`}
                  >
                    <span className="truncate">{STAGE_LABELS[stage] || stage}</span>
                    <span
                      className={`text-xs tabular-nums shrink-0 px-1.5 py-0.5 rounded-full ${
                        isActive ? 'bg-[hsl(var(--ds-surface))]/70' : 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </nav>
        </aside>

        <div className="flex-1 min-h-0 flex flex-col min-w-0">
        {sortedApplications.length === 0 ? (
        <div className={`${PORTAL_TABLE_CARD_STICKY} flex-1 min-h-0 flex items-center justify-center p-8 text-center text-[hsl(var(--ds-muted-fg))]`}>
          {hasActiveFilters
            ? 'No applications match the filters'
            : `No applications in ${STAGE_LABELS[activeStage] || activeStage}`}
        </div>
      ) : (
        <div className={`${PORTAL_TABLE_CARD_STICKY} flex-1 min-h-0 flex flex-col min-w-0`}>
          <div className={PORTAL_LIST_TABLE_SCROLL}>
            <table className={PORTAL_ATS_TABLE}>
              <thead className="bg-[hsl(var(--ds-muted))] border-b border-[hsl(var(--ds-border))] sticky top-0 z-30 shadow-[0_1px_0_0_rgb(226,232,240)]">
                <tr>
                  <th className="sticky-ats-sl text-left px-3 py-2 whitespace-nowrap">Sl No</th>
                  <SortableTh label="App ID" sortKey="id" sort={sort} onSort={toggleSort} className="sticky-ats-app px-3 py-2 whitespace-nowrap" />
                  <SortableTh label="Job Title" sortKey="jobTitle" sort={sort} onSort={toggleSort} className="sticky-ats-job px-3 py-2 whitespace-nowrap" />
                  <SortableTh label="Candidate" sortKey="candidate" sort={sort} onSort={toggleSort} className="sticky-ats-candidate px-3 py-2 whitespace-nowrap" />
                  <SortableTh label="Mobile" sortKey="mobile" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                  <SortableTh label="Email" sortKey="email" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                  <th className="text-left px-4 py-2 whitespace-nowrap">Match | Resume </th>
                  <SortableTh label="Company" sortKey="client" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                  <SortableTh label="Stage" sortKey="stage" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                  <SortableTh label="Current Status" sortKey="status" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                  <SortableTh label="Notes" sortKey="notes" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                  <SortableTh label="Date" sortKey="date" sort={sort} onSort={toggleSort} className="px-4 py-2 whitespace-nowrap" />
                  <th className={PORTAL_STICKY_ACTIONS_TH}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedApplications.map((app, idx) => (
                  <tr key={app.id} className="group border-b border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]">
                    <td className="sticky-ats-sl sticky-portal-scroll px-3 py-2 whitespace-nowrap text-xs text-[hsl(var(--ds-muted-fg))] align-middle">
                      {idx + 1}
                    </td>
                    <td className="sticky-ats-app sticky-portal-scroll px-3 py-2 whitespace-nowrap align-middle">
                      <div className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] truncate" title={app.id}>
                        {app.id}
                      </div>
                    </td>
                    <td className="sticky-ats-job sticky-portal-scroll px-3 py-2 whitespace-nowrap align-middle">
                      {canOpenStaffForms && app.jobId ? (
                        <button
                          type="button"
                          className="font-semibold text-sm truncate max-w-[9.5rem] text-left text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:underline underline-offset-2 block"
                          title={app.jobTitle || app.jobId || ''}
                          onClick={() => setProfileJobId(app.jobId)}
                        >
                          {app.jobTitle || app.jobId || '-'}
                        </button>
                      ) : (
                        <div
                          className="font-semibold text-sm truncate max-w-[9.5rem]"
                          title={app.jobTitle || app.jobId || ''}
                        >
                          {app.jobTitle || app.jobId || '-'}
                        </div>
                      )}
                    </td>
                    <td
                      className="sticky-ats-candidate sticky-portal-scroll px-3 py-2 whitespace-nowrap align-middle max-w-[16rem]"
                      title={
                        app.candidateMissing
                          ? 'Profile deleted — remove entry'
                          : app.candidateName || app.candidateId || ''
                      }
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {!app.candidateMissing && (
                          <div className="relative z-0 shrink-0">
                            <CandidateAvatarWithCompletion
                              candidate={{
                                photo: app.candidatePhoto,
                                profileCompletionPercent: app.candidateProfileCompletionPercent,
                                displayName: app.candidateName,
                                name: app.candidateName
                              }}
                              size="sm"
                              rounded="rounded-full"
                            />
                          </div>
                        )}
                        {canOpenProfile && app.candidateId && !app.candidateMissing ? (
                          <button
                            type="button"
                            className="text-sm font-medium truncate min-w-0 text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:underline underline-offset-2 text-left"
                            title="View candidate profile"
                            onClick={() => setProfileCandidateId(app.candidateId)}
                          >
                            {app.candidateName || app.candidateId || '-'}
                          </button>
                        ) : (
                          <div
                            className={`text-sm font-medium truncate min-w-0 ${app.candidateMissing ? 'text-amber-700' : 'text-[hsl(var(--ds-fg))]'}`}
                          >
                            {app.candidateName || app.candidateId || '-'}
                            {app.candidateMissing ? ' · deleted' : ''}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap align-middle">
                      <CandidatePhoneLink phone={app.candidatePhone} />
                    </td>
                    <td
                      className="px-4 py-2 whitespace-nowrap text-[hsl(var(--ds-muted-fg))] max-w-[12rem] truncate align-middle"
                      title={app.candidateEmail || ''}
                    >
                      {app.candidateEmail || '—'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap align-middle">
                      <div className="flex items-center gap-1">
                        {canViewMatchAnalytics && app.candidateId && !app.candidateMissing ? (
                          <button
                            type="button"
                            className="text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary-hover))] disabled:opacity-50"
                            disabled={matchAnalyticsLoadingId === app.id}
                            onClick={event => openMatchAnalytics(app, event)}
                            title="Match analytics"
                          >
                            <span className="inline-flex items-center justify-center w-7 h-7 hover:bg-[hsl(var(--ds-primary)/0.08)] rounded-lg">
                              {matchAnalyticsLoadingId === app.id ? (
                                <span className="inline-block w-3.5 h-3.5 border-2 border-[hsl(var(--ds-primary)/0.35)] border-t-[hsl(var(--ds-primary))] rounded-full animate-spin" />
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              )}
                            </span>
                          </button>
                        ) : null}
                        {app.candidateHasResume && app.candidateResumeLink ? (
                          <button
                            type="button"
                            className="text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))]"
                            onClick={() =>
                              setListResumePreview({
                                src: app.candidateResumeLink,
                                fileName: app.candidateResumeFileName || '',
                                candidateName: app.candidateName || '',
                                candidateId: app.candidateId
                              })
                            }
                            title="View resume"
                          >
                            <span className="inline-flex items-center justify-center w-7 h-7 hover:bg-[hsl(var(--ds-primary)/0.08)] rounded-lg">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </span>
                          </button>
                        ) : !canViewMatchAnalytics || app.candidateMissing || !app.candidateId ? (
                          <span className="text-xs text-[hsl(var(--ds-muted-fg))]">—</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {canOpenStaffForms && app.clientId ? (
                        <button
                          type="button"
                          className="text-xs text-[hsl(var(--ds-primary))] hover:text-[hsl(var(--ds-primary))] hover:underline underline-offset-2 text-left"
                          onClick={() => setProfileClientId(app.clientId)}
                        >
                          {app.clientName || '-'}
                        </button>
                      ) : (
                        <div className="text-xs text-[hsl(var(--ds-muted-fg))]">{app.clientName || '-'}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`${PORTAL_BADGE} py-1 ${STAGE_COLORS[app.stage] || 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]'}`}>
                        {STAGE_LABELS[app.stage] || app.stage || 'Submitted'}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs max-w-[200px]">
                      <div
                        className="truncate"
                        title={
                          app.stage === 'interview' && app.interviewScheduleDisplay
                            ? `${appCurrentStatusLabel(app)} — ${app.interviewScheduleDisplay.dateLabel}`
                            : appCurrentStatusLabel(app)
                        }
                      >
                        <span
                          className={`${PORTAL_BADGE} max-w-full truncate ${
                            app.stage === 'offer'
                              ? OFFER_STATUS_COLORS[app.offerStatus] || 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]'
                              : STAGE_COLORS[app.stage] || 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]'
                          }`}
                        >
                          {appCurrentStatusLabel(app)}
                        </span>
                        {app.stage === 'interview' && app.interviewScheduleDisplay && (
                          <span className="block text-[10px] text-[hsl(var(--ds-muted-fg))] truncate mt-0.5">
                            {app.interviewScheduleDisplay.dateLabel}
                          </span>
                        )}
                        {app.stage === 'offer' && app.offerDetails?.designation && (
                          <span
                            className="block text-[10px] text-[hsl(var(--ds-muted-fg))] truncate mt-0.5"
                            title={app.offerDetails.designation}
                          >
                            {app.offerDetails.designation}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div
                        className="text-xs text-[hsl(var(--ds-muted-fg))] max-w-xs truncate"
                        title={app.displayStatusNotes || app.statusNotes || ''}
                      >
                        {app.displayStatusNotes || app.statusNotes || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="text-xs text-[hsl(var(--ds-muted-fg))]">
                        {app.createdAt ? new Date(app.createdAt).toLocaleDateString() : '-'}
                      </div>
                    </td>
                    <td className={PORTAL_STICKY_ACTIONS_TD}>
                      {canManageAts && (
                        <ActionMenu align="right">
                          <ActionMenuItem onClick={() => setDetailApp(app)}>Details</ActionMenuItem>
                          <ActionMenuItem
                            onClick={() => {
                              setStageError('');
                              setStageModal(app);
                            }}
                          >
                            Update stage
                          </ActionMenuItem>
                          {app.candidateId && !app.candidateMissing && (
                            <ActionMenuItem
                              onClick={() =>
                                setHistoryCandidate({
                                  id: app.candidateId,
                                  name: app.candidateName || app.candidateId
                                })
                              }
                            >
                              Candidate history
                            </ActionMenuItem>
                          )}
                          <ActionMenuItem
                            danger
                            disabled={deleteApp.isLoading}
                            onClick={() => confirmDeleteApplication(app)}
                          >
                            Delete
                          </ActionMenuItem>
                        </ActionMenu>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
        </div>
      </div>

      {/* Submit Candidate Modal */}
      {showSubmitModal && user && (user.role === 'admin' || user.role === 'recruiter') && (
        <ModalShell
          title="Submit Candidate to Job"
          onClose={() => setShowSubmitModal(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              {createApp.isError && (
                <span className="text-sm text-red-600 mr-auto">{createApp.error?.response?.data?.message || 'Error submitting'}</span>
              )}
              <button type="button" className={PORTAL_BTN_SECONDARY} onClick={() => setShowSubmitModal(false)}>Cancel</button>
              <button
                type="button"
                className={PORTAL_BTN_PRIMARY}
                disabled={!form.candidateId || !form.jobId || createApp.isLoading}
                onClick={() => createApp.mutate(form)}
              >
                {createApp.isLoading ? 'Submitting...' : 'Submit'}
              </button>
            </>
          }
        >
            <p className="text-xs text-[hsl(var(--ds-muted-fg))] mb-3">
              Adds the candidate to internal ATS review. The hiring company is notified only after you use{' '}
              <strong>Submit for company review</strong> on the application row.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] block mb-1">Candidate</label>
                <SearchableCandidateSelect
                  candidates={candidates || []}
                  value={form.candidateId}
                  onChange={candidateId => setForm(f => ({ ...f, candidateId }))}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] block mb-1">Job</label>
                <SearchableJobSelect
                  jobs={jobs || []}
                  value={form.jobId}
                  onChange={jobId => setForm(f => ({ ...f, jobId }))}
                />
              </div>
              <div className="field-with-label">
                <textarea
                  className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
                  rows="3"
                  placeholder=" "
                  value={form.note}
                  onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                />
                <label>Note (Optional)</label>
              </div>
            </div>
        </ModalShell>
      )}

      {stageModal && user && user.role !== 'candidate' && (
        <StageUpdateModal
          key={stageModal.id}
          app={stageModal}
          userRole={user.role}
          onClose={() => {
            setStageModal(null);
            setStageError('');
          }}
          saving={mutation.isLoading}
          submitToClientSaving={submitToClientMutation.isLoading}
          errorMessage={stageError}
          onSave={payload => mutation.mutate({ id: stageModal.id, ...payload })}
          onSubmitToClient={note =>
            submitToClientMutation.mutate({ id: stageModal.id, note })
          }
        />
      )}

      {detailApp && (
        <ApplicationDetailPanel
          app={detailApp}
          userRole={user?.role}
          onClose={() => setDetailApp(null)}
        />
      )}

      <ResumePreviewModal
        open={!!listResumePreview?.src}
        onClose={() => setListResumePreview(null)}
        src={listResumePreview?.src}
        fileName={listResumePreview?.fileName}
        candidateName={listResumePreview?.candidateName}
        candidateId={listResumePreview?.candidateId}
      />

      {historyCandidate && (
        <CandidateHistoryModal
          candidateId={historyCandidate.id}
          candidateName={historyCandidate.name}
          onClose={() => setHistoryCandidate(null)}
        />
      )}

      {profileCandidateId && (
        <CandidateProfileModal
          candidateId={profileCandidateId}
          readOnly={profileReadOnly}
          onClose={() => setProfileCandidateId(null)}
        />
      )}

      {profileJobId && canOpenStaffForms && (
        <JobProfileModal jobId={profileJobId} onClose={() => setProfileJobId(null)} />
      )}

      {profileClientId && canOpenStaffForms && (
        <ClientProfileModal clientId={profileClientId} onClose={() => setProfileClientId(null)} />
      )}

      {matchAnalytics && (
        <MatchAnalyticsModal
          matchRow={matchAnalytics.matchRow}
          jobRequirements={matchAnalytics.jobRequirements}
          methodology={matchAnalytics.methodology}
          onClose={() => setMatchAnalytics(null)}
          onOpenProfile={
            canOpenProfile && !profileReadOnly
              ? id => {
                  setMatchAnalytics(null);
                  setProfileCandidateId(id);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
